interface YearMonth {
  year: number
  month: number
}

/**
 * Generates an array of YearMonth objects from start to end date (inclusive)
 */
function generateMonthRange(startDate: Date, endDate: Date): Array<YearMonth> {
  const start: YearMonth = { year: startDate.getFullYear(), month: startDate.getMonth() }
  const end: YearMonth = { year: endDate.getFullYear(), month: endDate.getMonth() }
  const count = (end.year - start.year) * 12 + (end.month - start.month) + 1

  return Array.from({ length: count }, (_, index) => {
    const totalMonths = start.year * 12 + start.month + index
    return {
      year: Math.floor(totalMonths / 12),
      month: totalMonths % 12,
    }
  })
}

/**
 * Groups sorted month keys into consecutive ranges
 */
function groupConsecutiveMonths(sortedMonthKeys: Array<string>): Array<{ start: string; end: string }> {
  if (sortedMonthKeys.length === 0) return []

  return sortedMonthKeys.reduce<Array<{ start: string; end: string }>>((groups, currentKey) => {
    const lastGroup = groups[groups.length - 1]

    if (!lastGroup) {
      return [{ start: currentKey, end: currentKey }]
    }

    const [prevYear, prevMonth] = lastGroup.end.split('-').map(Number)
    const [currYear, currMonth] = currentKey.split('-').map(Number)
    const isConsecutive = (currYear * 12 + currMonth) - (prevYear * 12 + prevMonth) === 1

    if (isConsecutive) {
      lastGroup.end = currentKey
      return groups
    }

    return [...groups, { start: currentKey, end: currentKey }]
  }, [])
}

/**
 * Formats a month range group into a readable string
 * Examples: "Apr 2026", "Apr - Nov 2026", "Dec 2026 - Feb 2027"
 */
function formatMonthRange(group: { start: string; end: string }): string {
  const [startYear, startMonth] = group.start.split('-').map(Number)
  const [endYear, endMonth] = group.end.split('-').map(Number)

  const startMonthName = new Date(startYear, startMonth).toLocaleString('en-US', { month: 'short' })
  const endMonthName = new Date(endYear, endMonth).toLocaleString('en-US', { month: 'short' })

  if (group.start === group.end) {
    return `${startMonthName} ${startYear}`
  }

  if (startYear === endYear) {
    return `${startMonthName} - ${endMonthName} ${endYear}`
  }

  return `${startMonthName} ${startYear} - ${endMonthName} ${endYear}`
}

/**
 * Formats flight deal travel periods into a human-readable string.
 *
 * Groups consecutive months together and formats as:
 * - "Apr - Nov 2026" for a single consecutive range
 * - "Apr - Jun 2026 · Aug - Nov 2026" for multiple non-consecutive ranges
 *
 * @param travelPeriods - Array of travel periods with departureDate and arrivalDate
 * @returns Formatted string like "Apr - Nov 2026" or "Apr - Jun 2026 · Aug - Nov 2026"
 */
export function formatFlightDealTravelPeriods(
  travelPeriods: Array<App.FlightDealTravelPeriod>,
): string {
  if (!travelPeriods || travelPeriods.length === 0) {
    return ''
  }

  // Extract all unique months from all periods
  const monthsSet = new Set<string>()
  for (const period of travelPeriods) {
    const months = generateMonthRange(new Date(period.departureDate), new Date(period.arrivalDate))
    months.forEach(ym => monthsSet.add(`${ym.year}-${String(ym.month).padStart(2, '0')}`))
  }

  const sortedMonths = Array.from(monthsSet).sort()
  const groups = groupConsecutiveMonths(sortedMonths)

  return groups.map(formatMonthRange).join(' · ')
}
